import type { CSSProperties, ReactElement } from 'react';
import type { HeroArtImages } from '@/assets/heroes';
import './hero-film.css';

export interface HeroArtProps {
  art: HeroArtImages;
  /**
   * Which part of the picture a short hero keeps, as a percentage down it (0 = the top edge, 100 = the bottom): the
   * 16:9 picture is cropped to the hero's height around this line. Default 45.
   */
  focusY?: number;
}

/**
 * The picture of a page hero that has no film: the same layout as `HeroFilm` (behind the words from 900 px, above
 * them below that — `hero-film.css`), with a still image as the whole thing. A 16:9 picture in three widths, AVIF
 * with a WebP fallback; it is the page's LCP, so it is in the server HTML and marked high priority. It is decoration
 * (`alt=""`): the page's title, lead and links carry all the meaning.
 */
export function HeroArt({ art, focusY }: HeroArtProps): ReactElement {
  const set = (images: HeroArtImages['avif']): string =>
    `${images[640]} 640w, ${images[1280]} 1280w, ${images[1920]} 1920w`;
  return (
    <div className="film__media">
      <div
        className="film__stage"
        aria-hidden="true"
        {...(focusY === undefined
          ? {}
          : { style: { '--film-y': `${String(focusY)}%` } as CSSProperties })}
      >
        <picture>
          <source type="image/avif" srcSet={set(art.avif)} sizes="100vw" />
          <source type="image/webp" srcSet={set(art.webp)} sizes="100vw" />
          <img
            className="film__poster"
            src={art.webp[1280]}
            width={1920}
            height={1080}
            alt=""
            decoding="async"
            fetchPriority="high"
          />
        </picture>
      </div>
      <div className="film__scrim" aria-hidden="true" />
    </div>
  );
}
